import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Banner for the Emails tabs explaining why an inbox is missing from the list.
 *
 * The `connected` flag on CRM ▸ My Profile is the source of truth: if it says Gmail is
 * connected we never claim otherwise here. A failed fetch on a connected account is a
 * token/API problem and is worded as such, so the two screens can't contradict each other.
 *
 * @param {Array<{id: string, name: string, status: string, profileConnected: boolean}>} users
 *   Users whose Gmail fetch returned something other than 'ok'.
 * @param {string} currentUserId - auth uid of the signed-in user
 * @param {Function} onConnect - navigates to My Profile
 */
const GmailConnectionNotice = ({ users = [], currentUserId, onConnect }) => {
  const mine = users.find(u => u.id === currentUserId);

  // Only nag about teammates whose connection broke — not ones who never connected Gmail
  const otherNames = users
    .filter(u => u.id !== currentUserId && u.profileConnected && u.status === 'needs_reauth')
    .map(u => u.name)
    .filter(Boolean);

  let mineTitle = null;
  let mineBody = null;
  let mineAction = null;

  if (mine) {
    if (!mine.profileConnected) {
      mineTitle = "Your Gmail isn't connected";
      mineBody = "Emails from your inbox can't be shown until you connect Gmail. This list may be incomplete.";
      mineAction = 'Connect Gmail';
    } else if (mine.status === 'needs_reauth') {
      mineTitle = 'Your Gmail needs to be reconnected';
      mineBody = 'Google stopped accepting the saved authorization, so your inbox was skipped. This list may be incomplete.';
      mineAction = 'Reconnect Gmail';
    } else {
      mineTitle = "Your Gmail couldn't be checked";
      mineBody = 'Gmail is connected, but the request failed just now. This list may be incomplete — try refreshing in a moment.';
    }
  }

  if (!mineTitle && otherNames.length === 0) return null;

  return (
    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 text-sm text-amber-800 dark:text-amber-200">
          {mineTitle ? (
            <>
              <p className="font-semibold">{mineTitle}</p>
              <p className="mt-1">{mineBody}</p>
              {otherNames.length > 0 && (
                <p className="mt-1">
                  Gmail also needs reconnecting for {otherNames.join(', ')}.
                </p>
              )}
              {mineAction && (
                <button
                  onClick={onConnect}
                  className="mt-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                >
                  {mineAction}
                </button>
              )}
            </>
          ) : (
            <>
              <p className="font-semibold">Some inboxes couldn't be checked</p>
              <p className="mt-1">
                Gmail needs reconnecting for {otherNames.join(', ')}, so this list may be incomplete.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GmailConnectionNotice;
